// Locale resolution — the explicit, authoritative chain for the customer portal.
//
// The portal is customer-facing and has NO per-user ERP row (no TenantUser), so
// its resolution chain is deliberately different from the Nexus ERP's
// (Nexus: user preference → tenant default → browser → 'en'). Here it is:
//
//     explicit customer picker (localStorage) → browser language → 'en'
//
// Kept CLIENT-SIDE ONLY — we never add a cross-repo call whose sole purpose is
// fetching a locale (cross-repo brief §11.2). If a linked Nexus partner's
// default language ever arrives in data the portal already receives, it could
// seed the picker as a lower-tier fallback — but only from data already on hand.
//
// This module is the single source of truth for the supported-locale set, the
// storage key, and normalization. Both the i18next language-detector config
// (index.ts) and the runtime picker (LocaleContext.tsx) key off the values here
// so the documented chain and the runtime mechanism cannot drift.

/**
 * Supported locales. `en` is the source/default; `es` is the only translation
 * for now. Adding a language later = one entry here + a new `locales/<code>/`
 * catalog set — the architecture must not hardcode a two-language assumption
 * (cross-repo brief §11.1).
 */
export const SUPPORTED_LOCALES = ['en', 'es'] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Source-of-truth / fallback locale. A missing `es` key shows its `en` value. */
export const DEFAULT_LOCALE: SupportedLocale = 'en';

/**
 * localStorage key holding the customer's explicit picker choice. Also used as
 * the language-detector's `lookupLocalStorage` + `caches` target so the picker
 * and the detector read/write the same slot.
 */
export const LOCALE_STORAGE_KEY = 'argo_portal_locale';

/** Type guard: is `value` one of the supported locales? */
export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Reduce any raw locale tag to a supported locale, or `null` if unsupported.
 * Matches on the language subtag only (`es-MX`, `es-419` → `es`; `en-US` → `en`)
 * so region variants resolve to the base language we translate.
 */
export function normalizeLocale(raw: string | null | undefined): SupportedLocale | null {
  if (!raw) return null;
  const base = raw.toLowerCase().split('-')[0];
  return isSupportedLocale(base) ? base : null;
}

/**
 * Resolve the initial locale using the explicit portal chain
 * (picker → browser → default). Pure and side-effect-free — safe to call from
 * anywhere (the LocaleContext, tests). The i18next detector implements the same
 * chain at runtime via the shared constants above; this function documents and
 * backstops it.
 */
export function resolveLocale(): SupportedLocale {
  // 1. Explicit customer picker (persisted).
  try {
    const stored = normalizeLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY));
    if (stored) return stored;
  } catch {
    // localStorage may be unavailable (private mode / SSR) — fall through.
  }

  // 2. Browser languages, in the user's stated order of preference.
  const navLangs =
    typeof navigator !== 'undefined'
      ? [...(navigator.languages ?? []), navigator.language]
      : [];
  for (const lang of navLangs) {
    const match = normalizeLocale(lang);
    if (match) return match;
  }

  // 3. Default.
  return DEFAULT_LOCALE;
}

/** Persist the customer's explicit picker choice. Best-effort (never throws). */
export function persistLocale(locale: SupportedLocale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Non-fatal — the in-memory i18next language still changes for this session.
  }
}
